import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Spinner,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Badge,
  VStack,
  Divider,
  Progress,
  HStack,
  Center,
  Select,
} from "@chakra-ui/react";
import axios from "axios";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { AdminShell } from "./shared/AdminShell";
import { AdminToolbar } from "./shared/AdminToolbar";
import { AdminMetricCard } from "./shared/AdminMetricCard";
import {
  MonthlySnapshotTable,
  MonthlySnapshotRow,
} from "./shared/MonthlySnapshotTable";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";
import {
  AdminAccessProvider,
  useAdminAccess,
} from "./shared/AdminAccess/AdminAccessContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const cardsBorderRadii ="8px";

type AnalyticsRangeMode = "lastMonths" | "month" | "year" | "custom" | "all";

type OrderItem = {
  productId?: string;
  name?: string;
  category?: string;
  quantity?: number;
  price?: number;
  sellerUserId?: string;
  ownerUserId?: string;
  ownerId?: string;
  accountUserId?: string;
  businessUserId?: string;
  createdBy?: string;
  userId?: string;
};

type Order = {
  _id: string;
  email?: string;
  total?: number;
  status?: string;
  createdAt?: string;
  items?: OrderItem[];
  userId?: string;
  ownerUserId?: string;
  ownerId?: string;
  sellerUserId?: string;
  accountUserId?: string;
  businessUserId?: string;
  createdBy?: string;
  metadata?: Record<string, any>;
};

type Booking = {
  _id: string;
  userId?: string;
  email?: string;
  status?: string;
  amount?: number;
  total?: number;
  price?: number;
  createdAt?: string;
  tripId?: string;
  routeName?: string;
  from?: string;
  to?: string;
  seats?: number;
  ownerUserId?: string;
  ownerId?: string;
  sellerUserId?: string;
  accountUserId?: string;
  businessUserId?: string;
  accommodationOwnerUserId?: string;
  carOwnerUserId?: string;
  tripOwnerUserId?: string;
  createdBy?: string;
  metadata?: Record<string, any>;
};

type UserRecord = {
  _id?: string;
  id?: string;
  uid?: string;
  firebaseUid?: string;
  email?: string;
  displayName?: string;
  name?: string;
  businessName?: string;
  companyName?: string;
  createdAt?: string;
  role?: string;
  status?: string;
};

type DeliveryRecord = {
  _id: string;
  status?: string;
  driverId?: string | null;
  createdAt?: string;
  pickup?: { address?: string };
  dropoff?: { address?: string };
  fee?: number;
  price?: number;
  amount?: number;
  userId?: string;
  ownerUserId?: string;
  ownerId?: string;
  sellerUserId?: string;
  accountUserId?: string;
  businessUserId?: string;
  senderUserId?: string;
  customerUserId?: string;
  requestedBy?: string;
  createdBy?: string;
  metadata?: Record<string, any>;
};

type MonthlyBucket = {
  key: string;
  label: string;
  count: number;
  revenue: number;
};


const safeNum = (v: any) => Number(v || 0);

const ALL_ACCOUNTS = "__all__";

const clean = (value: unknown) => String(value || "").trim();

const getUserRecordId = (user: UserRecord) =>
  clean(user.uid || user.firebaseUid || user.id || user._id);

const getUserRecordLabel = (user: UserRecord) => {
  const name = clean(
    user.businessName ||
      user.companyName ||
      user.displayName ||
      user.name ||
      user.email,
  );
  const id = getUserRecordId(user);
  const role = clean(user.role);

  if (name && role) return `${name} • ${role}`;
  if (name) return name;
  return id || "Unnamed account";
};

const collectOwnershipIds = (
  row: Record<string, any>,
  options: { includeUserFallback?: boolean } = {},
) => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = clean(value);
    if (id) ids.add(id);
  };

  const addMany = (values: unknown) => {
    if (!Array.isArray(values)) return;
    values.forEach(add);
  };

  add(row.ownerUserId);
  add(row.ownerId);
  add(row.sellerUserId);
  add(row.accountUserId);
  add(row.businessUserId);
  add(row.accommodationOwnerUserId);
  add(row.carOwnerUserId);
  add(row.tripOwnerUserId);
  add(row.senderUserId);
  add(row.requestedBy);
  add(row.createdBy);

  addMany(row.ownerUserIds);
  addMany(row.sellerUserIds);
  addMany(row.accountUserIds);
  addMany(row.businessUserIds);

  const metadata = row.metadata || {};

  add(metadata.ownerUserId);
  add(metadata.ownerId);
  add(metadata.sellerUserId);
  add(metadata.accountUserId);
  add(metadata.businessUserId);
  add(metadata.accommodationOwnerUserId);
  add(metadata.carOwnerUserId);
  add(metadata.tripOwnerUserId);
  add(metadata.senderUserId);
  add(metadata.requestedBy);
  add(metadata.createdBy);

  addMany(metadata.ownerUserIds);
  addMany(metadata.sellerUserIds);
  addMany(metadata.accountUserIds);
  addMany(metadata.businessUserIds);

  if (ids.size === 0 && options.includeUserFallback) {
    add(row.userId);
    add(row.customerUserId);
    add(metadata.userId);
    add(metadata.customerUserId);
  }

  return ids;
};

const orderBelongsToAccount = (order: Order, accountId: string) => {
  const normalizedAccountId = clean(accountId);
  if (!normalizedAccountId) return false;

  if (
    collectOwnershipIds(order as Record<string, any>, {
      includeUserFallback: true,
    }).has(normalizedAccountId)
  ) {
    return true;
  }

  return (order.items || []).some((item) =>
    collectOwnershipIds(item as Record<string, any>, {
      includeUserFallback: true,
    }).has(normalizedAccountId),
  );
};

const bookingBelongsToAccount = (booking: Booking, accountId: string) =>
  collectOwnershipIds(booking as Record<string, any>, {
    includeUserFallback: true,
  }).has(clean(accountId));

const deliveryBelongsToAccount = (
  delivery: DeliveryRecord,
  accountId: string,
) =>
  collectOwnershipIds(delivery as Record<string, any>, {
    includeUserFallback: true,
  }).has(clean(accountId));

function filterByRange<T extends { createdAt?: string }>(
  rows: T[],
  mode: AnalyticsRangeMode,
  selectedMonth: string,
  selectedYear: string,
  lastMonthsCount: number,
  customStartDate: string,
  customEndDate: string,
): T[] {
  const withDate = rows.filter(
    (r) => r.createdAt && !Number.isNaN(new Date(r.createdAt).getTime()),
  );

  if (mode === "all") return withDate;

  if (mode === "month") {
    return withDate.filter((r) => {
      const d = new Date(r.createdAt!);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      return key === selectedMonth;
    });
  }

  if (mode === "year") {
    return withDate.filter(
      (r) => String(new Date(r.createdAt!).getFullYear()) === selectedYear,
    );
  }

  if (mode === "lastMonths") {
    const now = new Date();
    const start = new Date(
      now.getFullYear(),
      now.getMonth() - (lastMonthsCount - 1),
      1,
    );
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    return withDate.filter((r) => {
      const d = new Date(r.createdAt!);
      return d >= start && d < end;
    });
  }

  if (mode === "custom") {
    if (!customStartDate && !customEndDate) return withDate;
    const start = customStartDate
      ? new Date(`${customStartDate}T00:00:00`)
      : null;
    const end = customEndDate
      ? new Date(`${customEndDate}T23:59:59.999`)
      : null;

    return withDate.filter((r) => {
      const d = new Date(r.createdAt!);
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
  }

  return withDate;
}

function buildMonthlyBuckets<T extends { createdAt?: string }>(
  rows: T[],
  getRevenue?: (row: T) => number,
): MonthlyBucket[] {
  const map = new Map<string, MonthlyBucket>();

  for (const row of rows) {
    if (!row.createdAt) continue;
    const d = new Date(row.createdAt);
    if (Number.isNaN(d.getTime())) continue;

    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString(undefined, {
      month: "short",
      year: "numeric",
    });

    if (!map.has(key)) {
      map.set(key, { key, label, count: 0, revenue: 0 });
    }

    const bucket = map.get(key)!;
    bucket.count += 1;
    bucket.revenue += getRevenue ? safeNum(getRevenue(row)) : 0;
  }

  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
}

function trendFromMonthly(monthly: MonthlyBucket[]) {
  const desc = [...monthly].sort((a, b) => b.key.localeCompare(a.key));
  if (desc.length < 2)
    return {
      countTrendPct: null as number | null,
      revenueTrendPct: null as number | null,
    };

  const current = desc[0];
  const prev = desc[1];

  return {
    countTrendPct:
      prev.count > 0 ? ((current.count - prev.count) / prev.count) * 100 : null,
    revenueTrendPct:
      prev.revenue > 0
        ? ((current.revenue - prev.revenue) / prev.revenue) * 100
        : null,
  };
}

function ApplicationAnalyticsDashboardContent() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);

  const {
    userId,
    role,
    isSuperAdmin,
    loading: accessLoading,
  } = useAdminAccess();

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [analyticsMode, setAnalyticsMode] =
    useState<AnalyticsRangeMode>("lastMonths");
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedYear, setSelectedYear] = useState<string>(() =>
    String(new Date().getFullYear()),
  );
  const [lastMonthsCount, setLastMonthsCount] = useState<number>(3);
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    indigo: "#6f7cff",
    violet: "#b05cff",
  };

  useEffect(() => {
    if (accessLoading || !userId) return;

    if (!isSuperAdmin) {
      setSelectedAccountId(userId);
      return;
    }

    setSelectedAccountId((current) => current || ALL_ACCOUNTS);
  }, [accessLoading, isSuperAdmin, userId]);

  const accountOptions = useMemo(() => {
    const seen = new Set<string>();

    const options = users
      .map((user) => ({
        value: getUserRecordId(user),
        label: getUserRecordLabel(user),
      }))
      .filter((option) => {
        if (!option.value || seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    if (isSuperAdmin) {
      return [
        { value: ALL_ACCOUNTS, label: "All profiles / accounts" },
        ...options,
      ];
    }

    const own = options.find((option) => option.value === userId);

    return [
      own || {
        value: userId,
        label: `My profile / account • ${role || "user"}`,
      },
    ];
  }, [isSuperAdmin, role, userId, users]);

  const effectiveAccountId = isSuperAdmin
    ? selectedAccountId || ALL_ACCOUNTS
    : userId;

  const selectedAccountLabel = useMemo(() => {
    if (effectiveAccountId === ALL_ACCOUNTS) {
      return "All profiles / accounts";
    }

    return (
      accountOptions.find((option) => option.value === effectiveAccountId)
        ?.label ||
      effectiveAccountId ||
      "Current account"
    );
  }, [accountOptions, effectiveAccountId]);

  const analyticsRangeLabel = useMemo(() => {
    if (analyticsMode === "all") return "All time";
    if (analyticsMode === "lastMonths") return `Last ${lastMonthsCount} months`;
    if (analyticsMode === "month") {
      const [y, m] = selectedMonth.split("-");
      const d = new Date(Number(y), Number(m) - 1, 1);
      return d.toLocaleString(undefined, { month: "long", year: "numeric" });
    }
    if (analyticsMode === "year") return `Year ${selectedYear}`;
    if (customStartDate && customEndDate)
      return `${customStartDate} → ${customEndDate}`;
    if (customStartDate) return `From ${customStartDate}`;
    if (customEndDate) return `Up to ${customEndDate}`;
    return "Custom range";
  }, [
    analyticsMode,
    lastMonthsCount,
    selectedMonth,
    selectedYear,
    customStartDate,
    customEndDate,
  ]);

  useEffect(() => {
    if (accessLoading || !userId) return;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const requestParams = isSuperAdmin
          ? undefined
          : {
              accountUserId: userId,
              ownerUserId: userId,
              userId,
            };

        const results = await Promise.allSettled([
          axios.get(`${API_BASE}/api/v1/orders`, {
            params: requestParams,
          }),
          axios.get(`${API_BASE}/api/v1/bookings`, {
            params: requestParams,
          }),
          axios.get(`${API_BASE}/api/v1/users`),
          axios.get(`${API_BASE}/api/delivery`, {
            params: requestParams,
          }),
        ]);

        setOrders(
          results[0].status === "fulfilled"
            ? results[0].value.data?.data || results[0].value.data || []
            : [],
        );
        setBookings(
          results[1].status === "fulfilled"
            ? results[1].value.data?.data || results[1].value.data || []
            : [],
        );
        setUsers(
          results[2].status === "fulfilled"
            ? results[2].value.data?.data || results[2].value.data || []
            : [],
        );
        setDeliveries(
          results[3].status === "fulfilled"
            ? results[3].value.data?.data || results[3].value.data || []
            : [],
        );
      } catch (e) {
        console.error(e);
        setError("Failed to load analytics dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [accessLoading, isSuperAdmin, userId]);

  const scopedOrders = useMemo(
    () =>
      effectiveAccountId === ALL_ACCOUNTS
        ? orders
        : orders.filter((order) =>
            orderBelongsToAccount(order, effectiveAccountId),
          ),
    [effectiveAccountId, orders],
  );

  const scopedBookings = useMemo(
    () =>
      effectiveAccountId === ALL_ACCOUNTS
        ? bookings
        : bookings.filter((booking) =>
            bookingBelongsToAccount(booking, effectiveAccountId),
          ),
    [bookings, effectiveAccountId],
  );

  const scopedUsers = useMemo(
    () =>
      effectiveAccountId === ALL_ACCOUNTS
        ? users
        : users.filter((user) => getUserRecordId(user) === effectiveAccountId),
    [effectiveAccountId, users],
  );

  const scopedDeliveries = useMemo(
    () =>
      effectiveAccountId === ALL_ACCOUNTS
        ? deliveries
        : deliveries.filter((delivery) =>
            deliveryBelongsToAccount(delivery, effectiveAccountId),
          ),
    [deliveries, effectiveAccountId],
  );

  // Range-filtered datasets
  const analyticsOrders = useMemo(
    () =>
      filterByRange(
        scopedOrders,
        analyticsMode,
        selectedMonth,
        selectedYear,
        lastMonthsCount,
        customStartDate,
        customEndDate,
      ),
    [
      scopedOrders,
      analyticsMode,
      selectedMonth,
      selectedYear,
      lastMonthsCount,
      customStartDate,
      customEndDate,
    ],
  );
  const analyticsBookings = useMemo(
    () =>
      filterByRange(
        scopedBookings,
        analyticsMode,
        selectedMonth,
        selectedYear,
        lastMonthsCount,
        customStartDate,
        customEndDate,
      ),
    [
      scopedBookings,
      analyticsMode,
      selectedMonth,
      selectedYear,
      lastMonthsCount,
      customStartDate,
      customEndDate,
    ],
  );
  const analyticsUsers = useMemo(
    () =>
      filterByRange(
        scopedUsers,
        analyticsMode,
        selectedMonth,
        selectedYear,
        lastMonthsCount,
        customStartDate,
        customEndDate,
      ),
    [
      scopedUsers,
      analyticsMode,
      selectedMonth,
      selectedYear,
      lastMonthsCount,
      customStartDate,
      customEndDate,
    ],
  );
  const analyticsDeliveries = useMemo(
    () =>
      filterByRange(
        scopedDeliveries,
        analyticsMode,
        selectedMonth,
        selectedYear,
        lastMonthsCount,
        customStartDate,
        customEndDate,
      ),
    [
      scopedDeliveries,
      analyticsMode,
      selectedMonth,
      selectedYear,
      lastMonthsCount,
      customStartDate,
      customEndDate,
    ],
  );

  // Orders analytics
  const ordersAnalytics = useMemo(() => {
    const totalOrders = analyticsOrders.length;
    const totalRevenue = analyticsOrders.reduce(
      (sum, o) => sum + safeNum(o.total),
      0,
    );
    const avgOrderValue = totalOrders ? totalRevenue / totalOrders : 0;

    const statusCounts = {
      draft: analyticsOrders.filter((o) => o.status === "draft").length,
      pending: analyticsOrders.filter((o) => o.status === "pending").length,
      paid: analyticsOrders.filter((o) => o.status === "paid").length,
      shipped: analyticsOrders.filter((o) => o.status === "shipped").length,
      delivered: analyticsOrders.filter((o) => o.status === "delivered").length,
      completed: analyticsOrders.filter((o) => o.status === "completed").length,
      returned: analyticsOrders.filter((o) => o.status === "returned").length,
      cancelled: analyticsOrders.filter((o) => o.status === "cancelled").length,
    };

    const allItems = analyticsOrders.flatMap((o) => o.items || []);
    let totalItemsQty = 0;
    const categoryQtyMap = new Map<string, number>();
    const categoryRevenueMap = new Map<string, number>();
    const productQtyMap = new Map<string, number>();

    for (const item of allItems) {
      const qty = safeNum(item.quantity || 1);
      const price = safeNum(item.price || 0);
      totalItemsQty += qty;

      const category = item.category || "uncategorized";
      categoryQtyMap.set(category, (categoryQtyMap.get(category) || 0) + qty);
      categoryRevenueMap.set(
        category,
        (categoryRevenueMap.get(category) || 0) + qty * price,
      );

      const product = item.name || "Unnamed product";
      productQtyMap.set(product, (productQtyMap.get(product) || 0) + qty);
    }

    const topCategories = Array.from(categoryQtyMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
    const topCategoryByRevenue = Array.from(categoryRevenueMap.entries()).sort(
      (a, b) => b[1] - a[1],
    )[0];
    const mostOrderedProduct = Array.from(productQtyMap.entries()).sort(
      (a, b) => b[1] - a[1],
    )[0];

    const monthly = buildMonthlyBuckets(analyticsOrders, (o) =>
      safeNum(o.total),
    );
    const latestMonth = [...monthly].sort((a, b) =>
      b.key.localeCompare(a.key),
    )[0];
    const bestRevenueMonth = [...monthly].sort(
      (a, b) => b.revenue - a.revenue,
    )[0];
    const bestOrdersMonth = [...monthly].sort((a, b) => b.count - a.count)[0];
    const trend = trendFromMonthly(monthly);

    return {
      totalOrders,
      totalRevenue,
      avgOrderValue,
      totalItemsQty,
      statusCounts,
      topCategories,
      topCategoryByRevenue,
      mostOrderedProduct,
      monthly,
      latestMonth,
      bestRevenueMonth,
      bestOrdersMonth,
      ...trend,
    };
  }, [analyticsOrders]);

  // Bookings analytics
  const bookingsAnalytics = useMemo(() => {
    const totalBookings = analyticsBookings.length;
    const totalBookingRevenue = analyticsBookings.reduce(
      (sum, b) => sum + safeNum(b.amount || b.total || b.price),
      0,
    );
    const avgBookingValue = totalBookings
      ? totalBookingRevenue / totalBookings
      : 0;

    const statusMap = new Map<string, number>();
    const routeMap = new Map<string, number>();
    let totalSeats = 0;

    for (const b of analyticsBookings) {
      const status = b.status || "unknown";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);

      const route =
        b.routeName ||
        (b.from && b.to ? `${b.from} → ${b.to}` : "unknown route");
      routeMap.set(route, (routeMap.get(route) || 0) + 1);

      totalSeats += safeNum(b.seats || 0);
    }

    const topRoutes = Array.from(routeMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
    const monthly = buildMonthlyBuckets(analyticsBookings, (b) =>
      safeNum(b.amount || b.total || b.price),
    );
    const latestMonth = [...monthly].sort((a, b) =>
      b.key.localeCompare(a.key),
    )[0];
    const trend = trendFromMonthly(monthly);

    return {
      totalBookings,
      totalBookingRevenue,
      avgBookingValue,
      totalSeats,
      statusCounts: Object.fromEntries(statusMap.entries()),
      topRoutes,
      monthly,
      latestMonth,
      ...trend,
    };
  }, [analyticsBookings]);

  // Users analytics
  const usersAnalytics = useMemo(() => {
    const totalUsers = scopedUsers.length;
    const usersInRange = analyticsUsers.length;
    const monthly = buildMonthlyBuckets(analyticsUsers);

    const roleMap = new Map<string, number>();
    const statusMap = new Map<string, number>();

    for (const u of scopedUsers) {
      const role = u.role || "user";
      roleMap.set(role, (roleMap.get(role) || 0) + 1);

      const st = u.status || "active";
      statusMap.set(st, (statusMap.get(st) || 0) + 1);
    }

    const topRoles = Array.from(roleMap.entries()).sort((a, b) => b[1] - a[1]);
    const topStatuses = Array.from(statusMap.entries()).sort(
      (a, b) => b[1] - a[1],
    );
    const trend = trendFromMonthly(monthly);

    return {
      totalUsers,
      usersInRange,
      monthly,
      topRoles,
      topStatuses,
      ...trend,
    };
  }, [scopedUsers, analyticsUsers]);

  // Deliveries analytics
  const deliveriesAnalytics = useMemo(() => {
    const total = analyticsDeliveries.length;

    const statusCounts = {
      requested: analyticsDeliveries.filter((d) => d.status === "requested")
        .length,
      assigned: analyticsDeliveries.filter((d) => d.status === "assigned")
        .length,
      accepted: analyticsDeliveries.filter((d) => d.status === "accepted")
        .length,
      in_transit: analyticsDeliveries.filter((d) => d.status === "in_transit")
        .length,
      delivered: analyticsDeliveries.filter((d) => d.status === "delivered")
        .length,
      cancelled: analyticsDeliveries.filter((d) => d.status === "cancelled")
        .length,
      returned: analyticsDeliveries.filter((d) => d.status === "returned")
        .length,
    };

    const assignedCount = analyticsDeliveries.filter(
      (d) => !!d.driverId,
    ).length;
    const assignmentRate = total ? (assignedCount / total) * 100 : 0;

    const routeMap = new Map<string, number>();
    for (const d of analyticsDeliveries) {
      const route = `${d.pickup?.address || "Unknown pickup"} → ${d.dropoff?.address || "Unknown dropoff"}`;
      routeMap.set(route, (routeMap.get(route) || 0) + 1);
    }
    const topRoutes = Array.from(routeMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    const monthly = buildMonthlyBuckets(analyticsDeliveries, (d) =>
      safeNum(d.amount || d.fee || d.price),
    );
    const trend = trendFromMonthly(monthly);

    return {
      total,
      assignedCount,
      assignmentRate,
      statusCounts,
      topRoutes,
      monthly,
      ...trend,
    };
  }, [analyticsDeliveries]);

  // Rows for monthly tables
  const ordersMonthlyRows: MonthlySnapshotRow[] = useMemo(
    () =>
      [...ordersAnalytics.monthly].reverse().map((m) => ({
        key: m.key,
        label: m.label,
        count: m.count,
        revenue: m.revenue,
      })),
    [ordersAnalytics.monthly],
  );

  const bookingsMonthlyRows: MonthlySnapshotRow[] = useMemo(
    () =>
      [...bookingsAnalytics.monthly].reverse().map((m) => ({
        key: m.key,
        label: m.label,
        count: m.count,
        revenue: m.revenue,
      })),
    [bookingsAnalytics.monthly],
  );

  const usersMonthlyRows: MonthlySnapshotRow[] = useMemo(
    () =>
      [...usersAnalytics.monthly].reverse().map((m) => ({
        key: m.key,
        label: m.label,
        count: m.count,
        revenue: 0,
      })),
    [usersAnalytics.monthly],
  );

  const deliveriesMonthlyRows: MonthlySnapshotRow[] = useMemo(
    () =>
      [...deliveriesAnalytics.monthly].reverse().map((m) => ({
        key: m.key,
        label: m.label,
        count: m.count,
        revenue: m.revenue,
      })),
    [deliveriesAnalytics.monthly],
  );

  const appOverview = useMemo(
    () => ({
      orders: analyticsOrders.length,
      bookings: analyticsBookings.length,
      users: analyticsUsers.length,
      deliveries: analyticsDeliveries.length,
    }),
    [
      analyticsOrders.length,
      analyticsBookings.length,
      analyticsUsers.length,
      analyticsDeliveries.length,
    ],
  );

  const tabStyle = {
    fontWeight: "700",
    border: `1px solid ${theme.border}`,
    borderRadius: cardsBorderRadii,
    px: 4,
    py: 3,
    boxShadow: theme.shadowSm,
    color: theme.text,
    bg: isDark ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.9)",
    _selected: {
      bg: isDark ? "rgba(39,183,255,0.10)" : "rgba(39,183,255,0.08)",
      borderColor: brand.electricBlue,
    },
    _hover: { borderColor: brand.electricBlue },
  };

  return (
    <AdminShell
      title="Application Analytics"
      subtitle={
        isSuperAdmin
          ? "Cross-module analytics for every profile/account, or a selected account."
          : "Cross-module analytics linked only to your profile/account."
      }
      badgeText={`${selectedAccountLabel} • ${analyticsRangeLabel}`}
    >
      {accessLoading || loading ? (
        <Flex
          minH="220px"
          justify="center"
          align="center"
          direction="column"
          gap={3}
        >
          <GlobalSpinner />
          <Text color={theme.textMuted}>
            {accessLoading
              ? "Checking account access..."
              : "Loading analytics..."}
          </Text>
        </Flex>
      ) : !userId ? (
        <Center minH="220px">
          <Text color={theme.textMuted}>
            Sign in before opening application analytics.
          </Text>
        </Center>
      ) : error ? (
        <Box
          p={4}
          borderRadius={cardsBorderRadii}
          bg={isDark ? "rgba(245,101,101,0.12)" : "red.50"}
        >
          <Text color={isDark ? "red.200" : "red.600"} fontWeight="700">
            {error}
          </Text>
        </Box>
      ) : (
        <>
          <Box
            mb={3}
            p={4}
            borderRadius={cardsBorderRadii}
            bg={theme.surface}
            border={`1px solid ${theme.border}`}
            boxShadow={theme.shadowSm}
          >
            <Flex
              align={{ base: "stretch", md: "center" }}
              justify="space-between"
              direction={{ base: "column", md: "row" }}
              gap={3}
            >
              <Box>
                <HStack spacing={2} flexWrap="wrap">
                  <Text color={theme.text} fontWeight="800">
                    Profile / Account
                  </Text>
                  <Badge
                    colorScheme={isSuperAdmin ? "purple" : "blue"}
                    borderRadius={cardsBorderRadii}
                  >
                    {isSuperAdmin
                      ? "Superadmin account filter"
                      : "Current account only"}
                  </Badge>
                </HStack>
                <Text mt={1} color={theme.textMuted} fontSize="sm">
                  {isSuperAdmin
                    ? "Select all accounts or one profile/account. Every metric and table below follows this selection."
                    : "Your signed-in Firebase UID is applied to orders, bookings, users, deliveries, revenue, trends, and monthly tables."}
                </Text>
              </Box>

              <Select
                value={effectiveAccountId}
                onChange={(event) => setSelectedAccountId(event.target.value)}
                isDisabled={!isSuperAdmin}
                maxW={{ base: "100%", md: "380px" }}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
                color={theme.text}
                fontWeight="700"
              >
                {accountOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Flex>

            <Text mt={2} fontSize="xs" color={theme.textMuted}>
              Active account: {selectedAccountLabel} • Role: {role || "user"} •
              UID: {userId}
            </Text>
          </Box>

          <AdminToolbar
            selects={[
              {
                value: analyticsMode,
                onChange: (v) => setAnalyticsMode(v as AnalyticsRangeMode),
                options: [
                  { value: "lastMonths", label: "Last N Months" },
                  { value: "month", label: "Single Month" },
                  { value: "year", label: "Year" },
                  { value: "custom", label: "Custom Range" },
                  { value: "all", label: "All Time" },
                ],
                maxW: { base: "100%", md: "190px" },
              },
              ...(analyticsMode === "lastMonths"
                ? [
                    {
                      value: String(lastMonthsCount),
                      onChange: (v: string) => setLastMonthsCount(Number(v)),
                      options: [2, 3, 6, 12].map((n) => ({
                        value: String(n),
                        label: `Last ${n} months`,
                      })),
                      maxW: { base: "100%", md: "170px" },
                    },
                  ]
                : []),
            ]}
            rightInfo={`Account: ${selectedAccountLabel} • Range: ${analyticsRangeLabel}`}
            accentGradient={theme.gradients?.secondaryButton}
          />

          {/* Extra range inputs only when needed */}
          <Box mb={3}>
            <Flex gap={3} wrap="wrap">
              {analyticsMode === "month" && (
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: cardsBorderRadii,
                    border: "1px solid #ccc",
                  }}
                />
              )}
              {analyticsMode === "year" && (
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius:cardsBorderRadii,
                    border: "1px solid #ccc",
                    width: 140,
                  }}
                />
              )}
              {analyticsMode === "custom" && (
                <>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: cardsBorderRadii,
                      border: "1px solid #ccc",
                    }}
                  />
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius:cardsBorderRadii,
                      border: "1px solid #ccc",
                    }}
                  />
                </>
              )}
            </Flex>
          </Box>

          {/* App overview */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, xl: 4 }}
            spacing={{ base: 2, md: 3 }}
            mb={3}
          >
            <AdminMetricCard
              label="Orders"
              value={appOverview.orders}
              helpText="in selected range"
            />
            <AdminMetricCard
              label="Bookings"
              value={appOverview.bookings}
              helpText="in selected range"
              accent={brand.indigo}
            />
            <AdminMetricCard
              label="Users"
              value={appOverview.users}
              helpText="signups in selected range"
              accent={brand.violet}
            />
            <AdminMetricCard
              label="Deliveries"
              value={appOverview.deliveries}
              helpText="in selected range"
              accent="#31C48D"
            />
          </SimpleGrid>

          <Tabs variant="unstyled">
            <TabList
              gap={{ base: 2, md: 3 }}
              flexWrap="wrap"
              p={{ base: 1, md: 2 }}
              borderRadius={cardsBorderRadii}
              bg={isDark ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.45)"}
              mb={3}
            >
              <Tab {...tabStyle}>Orders</Tab>
              <Tab {...tabStyle}>Bookings</Tab>
              <Tab {...tabStyle}>Users</Tab>
              <Tab {...tabStyle}>Deliveries</Tab>
            </TabList>

            <TabPanels>
              {/* ORDERS TAB */}
              <TabPanel p={0}>
                <SimpleGrid
                  columns={{ base: 1, lg: 4 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <AdminMetricCard
                    label="Orders in Range"
                    value={ordersAnalytics.totalOrders}
                    helpText={analyticsRangeLabel}
                  />
                  <AdminMetricCard
                    label="Orders Revenue"
                    value={`MWK ${Math.round(ordersAnalytics.totalRevenue).toLocaleString()}`}
                    helpText="Total order revenue"
                    accent={brand.indigo}
                  />
                  <AdminMetricCard
                    label="Avg Order Value"
                    value={`MWK ${Math.round(ordersAnalytics.avgOrderValue).toLocaleString()}`}
                    helpText="Average basket"
                    accent={brand.violet}
                  />
                  <AdminMetricCard
                    label="Items Ordered"
                    value={ordersAnalytics.totalItemsQty}
                    helpText="Total quantity sold"
                    accent="#31C48D"
                  />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, lg: 2 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      Orders Status Mix
                    </Text>
                    <SimpleGrid columns={2} spacing={2}>
                      {Object.entries(ordersAnalytics.statusCounts).map(
                        ([k, v]) => (
                          <Box
                            key={k}
                            borderRadius={cardsBorderRadii}
                            p={2.5}
                            bg={
                              isDark
                                ? "rgba(255,255,255,0.02)"
                                : theme.surfaceAlt
                            }
                          >
                            <Text
                              color={theme.textMuted}
                              fontSize="xs"
                              textTransform="capitalize"
                            >
                              {k}
                            </Text>
                            <Text color={theme.text} fontWeight="700">
                              {v}
                            </Text>
                          </Box>
                        ),
                      )}
                    </SimpleGrid>

                    <Divider borderColor={theme.border} my={3} />

                    <HStack justify="space-between">
                      <Text color={theme.textMuted}>Latest Month</Text>
                      <Text color={theme.text} fontWeight="700">
                        {ordersAnalytics.latestMonth?.label || "—"}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color={theme.textMuted}>Revenue Trend</Text>
                      <Text
                        color={
                          ordersAnalytics.revenueTrendPct == null
                            ? theme.textMuted
                            : ordersAnalytics.revenueTrendPct >= 0
                              ? "green.400"
                              : "red.400"
                        }
                        fontWeight="700"
                      >
                        {ordersAnalytics.revenueTrendPct == null
                          ? "—"
                          : `${ordersAnalytics.revenueTrendPct >= 0 ? "+" : ""}${ordersAnalytics.revenueTrendPct.toFixed(1)}%`}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color={theme.textMuted}>Orders Trend</Text>
                      <Text
                        color={
                          ordersAnalytics.countTrendPct == null
                            ? theme.textMuted
                            : ordersAnalytics.countTrendPct >= 0
                              ? "green.400"
                              : "red.400"
                        }
                        fontWeight="700"
                      >
                        {ordersAnalytics.countTrendPct == null
                          ? "—"
                          : `${ordersAnalytics.countTrendPct >= 0 ? "+" : ""}${ordersAnalytics.countTrendPct.toFixed(1)}%`}
                      </Text>
                    </HStack>
                  </Box>

                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      Top Categories & Product
                    </Text>

                    {ordersAnalytics.topCategories.length ? (
                      <VStack align="stretch" spacing={3}>
                        {(() => {
                          const maxVal =
                            ordersAnalytics.topCategories[0][1] || 1;
                          return ordersAnalytics.topCategories
                            .slice(0, 5)
                            .map(([category, qty]) => {
                              const pct = (qty / maxVal) * 100;
                              return (
                                <Box key={category}>
                                  <Flex justify="space-between" mb={1}>
                                    <Text
                                      color={theme.textMuted}
                                      fontSize="sm"
                                      textTransform="capitalize"
                                    >
                                      {category}
                                    </Text>
                                    <Badge colorScheme="purple">{qty}</Badge>
                                  </Flex>
                                  <Progress
                                    value={pct}
                                    size="xs"
                                    borderRadius={cardsBorderRadii}
                                    colorScheme="purple"
                                  />
                                </Box>
                              );
                            });
                        })()}

                        <Divider borderColor={theme.border} />

                        <Text color={theme.textMuted} fontSize="sm">
                          Top Category by Revenue
                        </Text>
                        <Text color={theme.text} fontWeight="700">
                          {ordersAnalytics.topCategoryByRevenue
                            ? `${ordersAnalytics.topCategoryByRevenue[0]} • MWK ${Math.round(ordersAnalytics.topCategoryByRevenue[1]).toLocaleString()}`
                            : "—"}
                        </Text>

                        <Text color={theme.textMuted} fontSize="sm">
                          Most Ordered Product
                        </Text>
                        <Text color={theme.text} fontWeight="700">
                          {ordersAnalytics.mostOrderedProduct?.[0] || "—"}
                        </Text>
                        <Text color={theme.textSubtle} fontSize="sm">
                          Qty: {ordersAnalytics.mostOrderedProduct?.[1] ?? 0}
                        </Text>
                      </VStack>
                    ) : (
                      <Text color={theme.textMuted}>
                        No order item/category data in selected range.
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>

                <MonthlySnapshotTable
                  title="Monthly Orders Snapshot"
                  subtitle={`${analyticsRangeLabel} • Orders and revenue by month`}
                  rows={ordersMonthlyRows}
                  countLabel="Orders"
                  revenueLabel="Revenue"
                  showRevenue
                />
              </TabPanel>

              {/* BOOKINGS TAB */}
              <TabPanel p={0}>
                <SimpleGrid
                  columns={{ base: 1, lg: 4 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <AdminMetricCard
                    label="Bookings in Range"
                    value={bookingsAnalytics.totalBookings}
                    helpText={analyticsRangeLabel}
                  />
                  <AdminMetricCard
                    label="Bookings Revenue"
                    value={`MWK ${Math.round(bookingsAnalytics.totalBookingRevenue).toLocaleString()}`}
                    helpText="Amount/total/price fields"
                    accent={brand.indigo}
                  />
                  <AdminMetricCard
                    label="Avg Booking Value"
                    value={`MWK ${Math.round(bookingsAnalytics.avgBookingValue).toLocaleString()}`}
                    helpText="Average booking value"
                    accent={brand.violet}
                  />
                  <AdminMetricCard
                    label="Seats Booked"
                    value={bookingsAnalytics.totalSeats}
                    helpText="If seats available"
                    accent="#31C48D"
                  />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, lg: 2 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      Bookings Status Mix
                    </Text>
                    {Object.keys(bookingsAnalytics.statusCounts).length ? (
                      <SimpleGrid columns={2} spacing={2}>
                        {Object.entries(bookingsAnalytics.statusCounts).map(
                          ([k, v]) => (
                            <Box
                              key={k}
                              borderRadius={cardsBorderRadii}
                              p={2.5}
                              bg={
                                isDark
                                  ? "rgba(255,255,255,0.02)"
                                  : theme.surfaceAlt
                              }
                            >
                              <Text
                                color={theme.textMuted}
                                fontSize="xs"
                                textTransform="capitalize"
                              >
                                {k}
                              </Text>
                              <Text color={theme.text} fontWeight="700">
                                {String(v)}
                              </Text>
                            </Box>
                          ),
                        )}
                      </SimpleGrid>
                    ) : (
                      <Text color={theme.textMuted}>
                        No booking status data in selected range.
                      </Text>
                    )}

                    <Divider borderColor={theme.border} my={3} />
                    <HStack justify="space-between">
                      <Text color={theme.textMuted}>Latest Month</Text>
                      <Text color={theme.text} fontWeight="700">
                        {bookingsAnalytics.latestMonth?.label || "—"}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color={theme.textMuted}>Bookings Trend</Text>
                      <Text
                        color={
                          bookingsAnalytics.countTrendPct == null
                            ? theme.textMuted
                            : bookingsAnalytics.countTrendPct >= 0
                              ? "green.400"
                              : "red.400"
                        }
                        fontWeight="700"
                      >
                        {bookingsAnalytics.countTrendPct == null
                          ? "—"
                          : `${bookingsAnalytics.countTrendPct >= 0 ? "+" : ""}${bookingsAnalytics.countTrendPct.toFixed(1)}%`}
                      </Text>
                    </HStack>
                  </Box>

                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      Top Routes
                    </Text>
                    {bookingsAnalytics.topRoutes.length ? (
                      <VStack align="stretch" spacing={3}>
                        {(() => {
                          const maxVal = bookingsAnalytics.topRoutes[0][1] || 1;
                          return bookingsAnalytics.topRoutes.map(
                            ([route, count]) => {
                              const pct = (count / maxVal) * 100;
                              return (
                                <Box key={route}>
                                  <Flex justify="space-between" mb={1} gap={2}>
                                    <Text
                                      color={theme.textMuted}
                                      fontSize="sm"
                                      noOfLines={1}
                                    >
                                      {route}
                                    </Text>
                                    <Badge colorScheme="blue">{count}</Badge>
                                  </Flex>
                                  <Progress
                                    value={pct}
                                    size="xs"
                                    borderRadius={cardsBorderRadii}
                                    colorScheme="blue"
                                  />
                                </Box>
                              );
                            },
                          );
                        })()}
                      </VStack>
                    ) : (
                      <Text color={theme.textMuted}>
                        No route data in selected range.
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>

                <MonthlySnapshotTable
                  title="Monthly Bookings Snapshot"
                  subtitle={`${analyticsRangeLabel} • Bookings and revenue by month`}
                  rows={bookingsMonthlyRows}
                  countLabel="Bookings"
                  revenueLabel="Revenue"
                  showRevenue
                />
              </TabPanel>

              {/* USERS TAB */}
              <TabPanel p={0}>
                <SimpleGrid
                  columns={{ base: 1, lg: 4 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <AdminMetricCard
                    label="Total User Base"
                    value={usersAnalytics.totalUsers}
                    helpText="All-time current users"
                  />
                  <AdminMetricCard
                    label="Signups in Range"
                    value={usersAnalytics.usersInRange}
                    helpText={analyticsRangeLabel}
                    accent={brand.indigo}
                  />
                  <AdminMetricCard
                    label="Signup Trend"
                    value={
                      usersAnalytics.countTrendPct == null
                        ? "—"
                        : `${usersAnalytics.countTrendPct >= 0 ? "+" : ""}${usersAnalytics.countTrendPct.toFixed(1)}%`
                    }
                    helpText="Latest vs previous month in range"
                    accent={brand.violet}
                  />
                  <AdminMetricCard
                    label="Tracked Roles"
                    value={usersAnalytics.topRoles.length}
                    helpText="Distinct user roles"
                    accent="#31C48D"
                  />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, lg: 2 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      User Roles
                    </Text>
                    {usersAnalytics.topRoles.length ? (
                      <VStack align="stretch" spacing={3}>
                        {(() => {
                          const maxVal = usersAnalytics.topRoles[0][1] || 1;
                          return usersAnalytics.topRoles.map(
                            ([role, count]) => {
                              const pct = (count / maxVal) * 100;
                              return (
                                <Box key={role}>
                                  <Flex justify="space-between" mb={1}>
                                    <Text
                                      color={theme.textMuted}
                                      textTransform="capitalize"
                                    >
                                      {role}
                                    </Text>
                                    <Badge colorScheme="purple">{count}</Badge>
                                  </Flex>
                                  <Progress
                                    value={pct}
                                    size="xs"
                                    borderRadius={cardsBorderRadii}
                                    colorScheme="purple"
                                  />
                                </Box>
                              );
                            },
                          );
                        })()}
                      </VStack>
                    ) : (
                      <Text color={theme.textMuted}>
                        No role data available.
                      </Text>
                    )}
                  </Box>

                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      User Status Breakdown
                    </Text>
                    {usersAnalytics.topStatuses.length ? (
                      <VStack align="stretch" spacing={3}>
                        {(() => {
                          const maxVal = usersAnalytics.topStatuses[0][1] || 1;
                          return usersAnalytics.topStatuses.map(
                            ([status, count]) => {
                              const pct = (count / maxVal) * 100;
                              return (
                                <Box key={status}>
                                  <Flex justify="space-between" mb={1}>
                                    <Text
                                      color={theme.textMuted}
                                      textTransform="capitalize"
                                    >
                                      {status}
                                    </Text>
                                    <Badge colorScheme="blue">{count}</Badge>
                                  </Flex>
                                  <Progress
                                    value={pct}
                                    size="xs"
                                    borderRadius={cardsBorderRadii}
                                    colorScheme="blue"
                                  />
                                </Box>
                              );
                            },
                          );
                        })()}
                      </VStack>
                    ) : (
                      <Text color={theme.textMuted}>
                        No user status data available.
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>

                <MonthlySnapshotTable
                  title="Monthly User Signups Snapshot"
                  subtitle={`${analyticsRangeLabel} • User signups by month`}
                  rows={usersMonthlyRows}
                  countLabel="Signups"
                  showRevenue={false}
                />
              </TabPanel>

              {/* DELIVERIES TAB */}
              <TabPanel p={0}>
                <SimpleGrid
                  columns={{ base: 1, lg: 4 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <AdminMetricCard
                    label="Deliveries in Range"
                    value={deliveriesAnalytics.total}
                    helpText="Selected time range"
                    accent={brand.electricBlue}
                  />
                  <AdminMetricCard
                    label="Assigned Deliveries"
                    value={deliveriesAnalytics.assignedCount}
                    helpText={`${deliveriesAnalytics.assignmentRate.toFixed(1)}% assignment rate`}
                    accent={brand.indigo}
                  />
                  <AdminMetricCard
                    label="Delivered"
                    value={deliveriesAnalytics.statusCounts.delivered}
                    helpText="Completed deliveries"
                    accent="#31C48D"
                  />
                  <AdminMetricCard
                    label="In Transit"
                    value={deliveriesAnalytics.statusCounts.in_transit}
                    helpText="Currently moving"
                    accent="#F59E0B"
                  />
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, lg: 2 }}
                  spacing={{ base: 2, md: 3 }}
                  mb={3}
                >
                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      Delivery Status Mix
                    </Text>

                    <SimpleGrid columns={2} spacing={2} mb={3}>
                      {Object.entries(deliveriesAnalytics.statusCounts).map(
                        ([k, v]) => (
                          <Box
                            key={k}
                            borderRadius={cardsBorderRadii}
                            p={2.5}
                            bg={
                              isDark
                                ? "rgba(255,255,255,0.02)"
                                : theme.surfaceAlt
                            }
                          >
                            <Text
                              color={theme.textMuted}
                              fontSize="xs"
                              textTransform="capitalize"
                            >
                              {k}
                            </Text>
                            <Text color={theme.text} fontWeight="700">
                              {v}
                            </Text>
                          </Box>
                        ),
                      )}
                    </SimpleGrid>

                    <Divider borderColor={theme.border} my={3} />

                    <HStack justify="space-between">
                      <Text color={theme.textMuted}>Trend (count)</Text>
                      <Text
                        color={
                          deliveriesAnalytics.countTrendPct == null
                            ? theme.textMuted
                            : deliveriesAnalytics.countTrendPct >= 0
                              ? "green.400"
                              : "red.400"
                        }
                        fontWeight="700"
                      >
                        {deliveriesAnalytics.countTrendPct == null
                          ? "—"
                          : `${deliveriesAnalytics.countTrendPct >= 0 ? "+" : ""}${deliveriesAnalytics.countTrendPct.toFixed(1)}%`}
                      </Text>
                    </HStack>
                  </Box>

                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={theme.surface}
                    boxShadow={theme.shadowSm}
                    border={`1px solid ${theme.border}`}
                    p={4}
                  >
                    <Text color={theme.text} fontWeight="800" mb={3}>
                      Top Routes
                    </Text>
                    {deliveriesAnalytics.topRoutes.length ? (
                      <VStack align="stretch" spacing={3}>
                        {(() => {
                          const maxVal =
                            deliveriesAnalytics.topRoutes[0][1] || 1;
                          return deliveriesAnalytics.topRoutes.map(
                            ([route, count]) => {
                              const pct = (count / maxVal) * 100;
                              return (
                                <Box key={route}>
                                  <Flex justify="space-between" mb={1} gap={2}>
                                    <Text
                                      color={theme.textMuted}
                                      fontSize="sm"
                                      noOfLines={1}
                                    >
                                      {route}
                                    </Text>
                                    <Badge colorScheme="blue">{count}</Badge>
                                  </Flex>
                                  <Progress
                                    value={pct}
                                    size="xs"
                                    borderRadius={cardsBorderRadii}
                                    colorScheme="blue"
                                  />
                                </Box>
                              );
                            },
                          );
                        })()}
                      </VStack>
                    ) : (
                      <Text color={theme.textMuted}>
                        No route data in selected range.
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>

                <MonthlySnapshotTable
                  title="Monthly Deliveries Snapshot"
                  subtitle={`${analyticsRangeLabel} • Deliveries by month`}
                  rows={deliveriesMonthlyRows}
                  countLabel="Deliveries"
                  revenueLabel="Revenue (optional)"
                  showRevenue
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
    </AdminShell>
  );
}

const ApplicationAnalyticsDashboard: React.FC = () => (
  <AdminAccessProvider>
    <ApplicationAnalyticsDashboardContent />
  </AdminAccessProvider>
);

export default ApplicationAnalyticsDashboard;
